/** @format */
import { Divider } from '@mpb-com/feather/components/atoms/divider';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { Trans, useTranslation } from 'next-i18next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import qs from 'qs';
import React, { FunctionComponent, ReactNode, useContext, useRef } from 'react';
import styled from 'styled-components';

import { SearchFacet } from '@toucan/common/services/search/constants';
import { getSearchParamKey } from '@toucan/common/services/search/helpers';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import {
    NextRoute,
    WrappedLink,
    getCurrentRoute,
    setSearchParamsForSearchResults,
} from '@toucan/client/global/nextjs';

import { scrollToElement, scrollToTop } from '@toucan/client/core/scroll';

import { GridRepeater } from '@toucan/client/shared/components/grid-repeater';
import { Link } from '@toucan/client/shared/components/link';
import {
    Pagination,
    PaginationItemProps,
} from '@toucan/client/shared/components/pagination';

import { ButtonDivider } from '@toucan/client/blocks/components/button-divider';

import { useSearchParams } from '@toucan/client/features/search/components/model-search-results/hooks/use-search-params';

import {
    PaginationControlsPosition,
    PaginationVariant,
    SearchMessageType,
    SearchResultsVariant,
} from './constants';
import { getSearchResultsGridColumns } from './helpers';
import { styles } from './search-results-display.styles';
import { SearchResultsProps, StyledSearchResultsProps } from './types';

const StyledSearchResults = styled.div<StyledSearchResultsProps>`
    ${styles}
`;

export const SearchResultsDisplay: FunctionComponent<SearchResultsProps> = ({
    isLoading = false,
    paginationVariant = PaginationVariant.PAGE_SELECT,
    resultsInView,
    resultsInViewCount,
    resultsPerPage = 52,
    numResultSkeletons = 0,
    displayAllResults = false,
    totalResultsCount,
    page,
    onSeeAllClick,
    variant,
    onCreateAlert,
    displayOutOfStock = false,
    noInStockResults = false,
    ...otherProps
}: SearchResultsProps) => {
    const { t } = useTranslation();
    const router = useRouter();
    const { selectedFilters } = useSearchParams();

    const isMobile = useMediaQuery(MediaQuery.MOBILE);
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const inStockModelsURL = useRef<NextRoute>();

    const { activeMarket } = useContext(MarketContext);

    const topOfResults = useRef<HTMLDivElement | null>(null);

    const totalPages =
        totalResultsCount !== -1
            ? Math.ceil(totalResultsCount / resultsPerPage)
            : -1;

    const paginationItemsLimit = isMobile ? 5 : 7;
    const resultsInViewsLength =
        (resultsInViewCount ?? resultsInView.length) - numResultSkeletons;

    const isOutOfBounds = page >= totalPages;
    const isLoadingResults = totalResultsCount === -1;

    const hasResults = totalResultsCount > 0;

    // isOutOfBounds is true when we have no results, and we want to index the first page in this case
    const shouldNotIndex =
        (hasResults && isOutOfBounds) || (!hasResults && page > 0);

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displayShowAllControl =
        !displayAllResults && totalResultsCount > resultsInView.length;

    function handleBackToInStockModels() {
        scrollToTop();

        // if we don't have a url saved to restore then we just remove the out of stock param
        // this happens if we come back to this page from a saved url
        if (!inStockModelsURL.current) {
            setSearchParamsForSearchResults(router, {
                page: undefined,
                showAll: undefined,
                sort: undefined,
                originalQuery: undefined,
                outOfStock: undefined,
            });
            return;
        }

        // restore our saved url
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Legacy code: do not use as an example.
        router.push(
            {
                pathname: inStockModelsURL.current.pathname,
                query: inStockModelsURL.current.query,
            },
            inStockModelsURL.current.asPath
        );
    }

    /**
     * Handler for showing the out of stock models.
     */
    function handleShowOutOfStockModels() {
        // first we store the current url so we can later restore it
        inStockModelsURL.current = getCurrentRoute(router);

        const filtersToRemoveForOutOfStock = [
            SearchFacet.productConditionStarRating,
            SearchFacet.productPrice,
        ];

        const filters = Object.fromEntries(
            Object.entries(selectedFilters).map(([key, value]) => {
                const searchParamKey = getSearchParamKey(key);

                if (filtersToRemoveForOutOfStock.includes(key as SearchFacet)) {
                    return [searchParamKey, undefined];
                }

                return [searchParamKey, value];
            })
        );

        // then we clear our search params, setting show out of stock
        setSearchParamsForSearchResults(router, {
            page: undefined,
            showAll: undefined,
            sort: undefined,
            originalQuery: undefined,
            outOfStock: true,
            ...filters,
        });

        scrollToTop();
    }

    function handlePaginationUpdate(pageIndex: number) {
        const page = pageIndex === 0 ? undefined : pageIndex + 1;

        setSearchParamsForSearchResults(router, { page }, true);

        scrollToTop();
    }

    /**
     * Return a SearchMessageType to control the message (if any) that is displayed at the bottom of
     * the search listing page. Returns null when no message should be displayed
     *
     * @returns {SearchMessageType | null}
     */
    function getSearchMessageType(): SearchMessageType | null {
        if (isLoadingResults) {
            return null;
        }

        const isLastPage = displayAllResults
            ? totalResultsCount === resultsInView.length
            : page + 1 >= totalPages;

        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const noResults = totalPages < 1 && resultsInViewsLength === 0;

        if (noResults) {
            return SearchMessageType.NO_RESULTS;
        }

        if (isOutOfBounds) {
            return SearchMessageType.OUT_OF_BOUNDS;
        }

        if (isLastPage || displayAllResults) {
            if (variant === SearchResultsVariant.PRODUCT) {
                return SearchMessageType.END_OF_RESULTS_PRODUCT;
            }
            return SearchMessageType.END_OF_RESULTS_MODEL;
        }

        return null;
    }

    const searchMessageType = getSearchMessageType();

    /**
     * Return the page href for a particular pagination link, removing the page param if
     * the page is 1. This allows the generated URLs to be correct in markup.
     *
     * @param {number} pageNumber The zero indexed page
     * @return {string} The href to add to the pagination link
     */
    function getPageHref(pageNumber: number): string {
        const newPageNumber = pageNumber + 1;

        const preparedParams = qs.stringify(
            {
                ...router.query,
                slug: undefined,
                page: newPageNumber > 1 ? newPageNumber : undefined,
            },
            { addQueryPrefix: true }
        );

        if (!router.query.slug) {
            return '';
        }

        const path = router.asPath.split('?')[0];

        const url = `/${activeMarket.slug}${path}`;

        return `${url}${preparedParams}`;
    }

    const paginationItems: PaginationItemProps[] = [];
    for (let i = 0; i < totalPages; i++) {
        paginationItems.push({
            text: `${i + 1}`,
            href: getPageHref(i),
        });
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displaySeeMoreButton =
        paginationVariant === PaginationVariant.VIEW_MORE &&
        page + 1 < totalPages &&
        !displayAllResults;

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displaySeeLessButton =
        paginationVariant === PaginationVariant.VIEW_MORE &&
        page + 1 >= totalPages &&
        page !== 0 &&
        !displayAllResults;

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displayContentLine =
        paginationVariant === PaginationVariant.VIEW_MORE &&
        !displaySeeMoreButton &&
        !displaySeeLessButton;

    /**
     * Get the total results as a ReactNode
     *
     * @returns {ReactNode} A react node representing the total results
     */
    function renderTotalResults() {
        if (isLoading || totalResultsCount === -1) {
            return (
                <Skeleton
                    data-testid="search-results__results-skeleton"
                    className="total-results-skeleton"
                    variant={SkeletonVariant.TEXT}
                />
            );
        }

        if (isSmallTablet) {
            return (
                <span data-testid="search-results__mobile-result-count">
                    {t('{{totalResultsCount}} results', {
                        totalResultsCount: totalResultsCount.toString(),
                    })}
                </span>
            );
        }

        return (
            <span data-testid="search-results__desktop-result-count">
                {t(
                    'Showing {{displayedResults}} of {{totalResultsCount}} results',
                    {
                        displayedResults: displayAllResults
                            ? totalResultsCount.toString()
                            : resultsInViewsLength.toString(),
                        totalResultsCount: totalResultsCount.toString(),
                    }
                )}
            </span>
        );
    }

    /**
     * Get the pagination controls as a ReactNode
     *
     * @returns {ReactNode} - A react node representing the pagination controls
     */
    function renderPaginationControls(
        position: PaginationControlsPosition
    ): ReactNode {
        const isPageSelectVariant =
            paginationVariant === PaginationVariant.PAGE_SELECT;

        const isBottomPosition = position === PaginationControlsPosition.BOTTOM;

        const shouldRenderPagination =
            !displayAllResults && isPageSelectVariant && isBottomPosition;

        const shouldRenderTotalResults = isPageSelectVariant // eslint-disable-line no-nested-ternary -- Legacy code: do not use as an example.
            ? isMobile
                ? !isBottomPosition
                : true
            : true;

        return (
            <div className="pagination">
                <div
                    className="results-meta"
                    data-testid="search-results__meta"
                >
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {shouldRenderTotalResults && renderTotalResults()}

                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {displayShowAllControl && (
                        <Link
                            onClick={onSeeAllClick}
                            data-testid="search-results__see-all"
                        >
                            {t('See all')}
                        </Link>
                    )}
                </div>
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {shouldRenderPagination && (
                    <Pagination
                        currentItem={page}
                        items={paginationItems}
                        itemLimit={paginationItemsLimit}
                        showFirstLastButtons={true}
                        showPrevNextButtons={true}
                        itemLabel={(item) =>
                            t('Page {{pageNumber}}', {
                                pageNumber: item + 1,
                            })
                        }
                        onPage={handlePaginationUpdate}
                        data-testid="search-results__pagination"
                    />
                )}
            </div>
        );
    }

    /**
     *
     * Get a message to display in the search results as a ReactNode
     * @param {ReactNode} title - A title for the results message
     * @param {ReactNode} subtitle - A subtitle for the results message
     * @param {ReactNode} [link] - An optional link to be displayed. When nothing is passed a default link is displayed
     *
     * @returns {ReactNode} - A react node representing a search results message
     */
    function getSearchResultsMessage(
        title: ReactNode,
        subtitle: ReactNode,
        link?: ReactNode
    ): ReactNode {
        link =
            link ??
            (variant === SearchResultsVariant.MODEL &&
                !noInStockResults &&
                (displayOutOfStock ? (
                    <Link
                        onClick={handleBackToInStockModels}
                        className="back-to-in-stock-models-button"
                        data-testid="search-results__back-to-in-stock-models-button"
                    >
                        {t('Go back to models in stock')}
                    </Link>
                ) : (
                    <Link
                        onClick={handleShowOutOfStockModels}
                        className="find-out-of-stock-models-button"
                        data-testid="search-results__find-out-of-stock-models-button"
                    >
                        {t('Find out of stock models')}
                    </Link>
                )));

        return (
            <div
                className="results-message"
                data-testid="search-results__results-message"
            >
                <Heading size={HeadingSize.H4} as={'div'} className="title">
                    {title}
                </Heading>
                {subtitle !== null && <p className="subtitle">{subtitle}</p>}
                {link}
            </div>
        );
    }

    /**
     * Conditionally returns a message to be shown in the search results.
     *
     * @returns {ReactNode | null} The message to display as a ReactNode or null
     * when there is nothing to display
     */
    function getSearchMessage(): ReactNode {
        switch (searchMessageType) {
            case SearchMessageType.OUT_OF_BOUNDS:
                return getSearchResultsMessage(
                    t("You've gone too far!"),
                    t("We don't have this many results to show you")
                );

            case SearchMessageType.END_OF_RESULTS_PRODUCT:
                return getSearchResultsMessage(
                    t("That's all of our available options"),
                    onCreateAlert ? (
                        <Trans
                            components={[
                                <Link key={0} onClick={onCreateAlert}>
                                    $0
                                </Link>,
                            ]}
                        >
                            {t(
                                'If none of these are suitable, <0>create an alert</0>. We add more products every day.'
                            )}
                        </Trans>
                    ) : null
                );
            case SearchMessageType.END_OF_RESULTS_MODEL:
                return getSearchResultsMessage(
                    t("That's all of our results"),
                    t(
                        "If you didn't find what you were looking for, try changing your search criteria."
                    )
                );
            case SearchMessageType.NO_RESULTS:
                return getSearchResultsMessage(
                    t('We couldn’t find any results.'),
                    t('Try changing your search criteria.'),
                    <div
                        className="no-results-message"
                        data-testid="search-results__no-results"
                    >
                        <Trans
                            components={[
                                <Link
                                    key={0}
                                    onClick={handleShowOutOfStockModels}
                                >
                                    $0
                                </Link>,
                                <WrappedLink
                                    key={1}
                                    href="/search?q=*"
                                    as={`/${activeMarket.slug}/${t('search', {
                                        ns: 'urls',
                                    })}?q=*`}
                                    className="show-all-models-link"
                                    passHref={true}
                                >
                                    $1
                                </WrappedLink>,
                            ]}
                        >
                            {variant === SearchResultsVariant.MODEL &&
                            !noInStockResults &&
                            !displayOutOfStock
                                ? t(
                                      'Alternatively, <0>find out of stock models</0> or <1>why not explore all of our camera gear</1>'
                                  )
                                : t(
                                      '<1>Why not explore all of our camera gear</1>'
                                  )}
                        </Trans>
                    </div>
                );
            default:
                return null;
        }
    }

    return (
        <StyledSearchResults
            paginationVariant={paginationVariant}
            resultsInView={resultsInView}
            resultsPerPage={resultsPerPage}
            totalResultsCount={totalResultsCount}
            page={page}
            variant={variant}
            {...otherProps}
        >
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {shouldNotIndex && (
                <Head>
                    <meta name="robots" content="noindex" />
                </Head>
            )}

            <section className="results">
                <div ref={topOfResults} />
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {displayOutOfStock &&
                    !noInStockResults &&
                    variant === SearchResultsVariant.MODEL && (
                        <div className="back-to-in-stock-models">
                            {t('Showing out of stock models only.')}{' '}
                            <Link
                                onClick={handleBackToInStockModels}
                                className="back-to-in-stock-models-button"
                            >
                                {t('Go back to models in stock')}
                            </Link>
                        </div>
                    )}

                <GridRepeater
                    data-testid="search-results__grid"
                    gridGap="var(--layout-spacing-12)"
                    className="search-results-grid"
                    gridColumns={getSearchResultsGridColumns()}
                >
                    {resultsInView.map((item: ReactNode) => item)}
                </GridRepeater>
            </section>

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {displaySeeMoreButton && (
                <ButtonDivider
                    className="show-more"
                    onClick={() => {
                        handlePaginationUpdate(page + 1);
                    }}
                    data-testid="search-results__see-more-button"
                >
                    {t('See more', {
                        context: 'Product listing',
                    })}
                </ButtonDivider>
            )}

            <section className="pagination-bottom">
                {!isLoading && getSearchMessage()}
                {paginationVariant === PaginationVariant.PAGE_SELECT &&
                    !displayAllResults &&
                    totalPages > 1 &&
                    renderPaginationControls(PaginationControlsPosition.BOTTOM)}
            </section>

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {displaySeeLessButton && (
                <ButtonDivider
                    className="see-less"
                    data-testid="search-results__see-less-button-divider"
                    onClick={() => {
                        handlePaginationUpdate(0);
                        scrollToElement(topOfResults.current);
                    }}
                >
                    {t('See less', {
                        context: 'Product listing',
                    })}
                </ButtonDivider>
            )}

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {displayContentLine && <Divider className="content-line" />}
        </StyledSearchResults>
    );
};
